import { ASSET_CLASS_IDS } from "~/consts";
import FnlSummariesPlatform from "~/schemas/dbt/FnlSummariesPlatform";
import { calculatePercentChange } from "~/utils/calculatePercentChange";
import { MeasureValue } from "~/types";

// // // //

interface JurisdictionCountryStats {
    id: number;
    name: string;
    slug: string;
    color_hex: string | null;
    icon_url: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    bridged_token_market_cap_dollar: MeasureValue;
    circulating_asset_value_dollar: MeasureValue;
}

interface AssetClassStats {
    id: number;
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    bridged_token_market_cap_dollar: MeasureValue;
    circulating_asset_value_dollar: MeasureValue;
    holding_addresses_count: MeasureValue;
}

interface NetworkStats {
    id: number;
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    bridged_token_market_cap_dollar: MeasureValue;
    circulating_asset_value_dollar: MeasureValue;
    holding_addresses_count: MeasureValue;
}

interface IssuerStats {
    id: string; // QUESTION - why this mismatch? can we fix thi
    name: string;
    slug: string;
    icon_url: string | null;
    color_hex: string | null;
    asset_count: number;
    description: string | null;
    bridged_token_value_dollar: MeasureValue;
    bridged_token_market_cap_dollar: MeasureValue;
    circulating_asset_value_dollar: MeasureValue;
}

interface TokenizationTypeStats {
    type: "distributed" | "represented";
    bridged_token_value_dollar: MeasureValue;
    asset_count: number;
    token_count: number;
}

export interface Platform extends FnlSummariesPlatform {
    name: string;
    slug: string;
    website: string | null;
    asset_class_stats: AssetClassStats[];
    issuer_stats: IssuerStats[];
    jurisdiction_country_stats: JurisdictionCountryStats[];
    network_stats: NetworkStats[];
    tokenization_type_stats: TokenizationTypeStats[];
    bridged_token_market_cap_dollar: MeasureValue;
    bridged_token_value_dollar: MeasureValue;
    circulating_asset_value_dollar: MeasureValue;
    holding_addresses_count: MeasureValue;
    trailing_30_day_transfer_volume: MeasureValue;
    trailing_30_day_active_addresses_count: MeasureValue;
    adjusted_daily_mints_dollar: MeasureValue;
    adjusted_daily_mints_token: MeasureValue;
}

export enum PlatformColumns {
    id = "id",
    name = "name",
    slug = "slug",
    // // // //
    assetCount = "asset_count",
    bridgedTokenMarketCapDollar = "bridged_token_market_cap_dollar",
    bridgedTokenValueDollar = "bridged_token_value_dollar",
    circulatingAssetValueDollar = "circulating_asset_value_dollar",
    trailing30DayTransferVolume = "trailing_30_day_transfer_volume",
    holdingAddressesCount = "holding_addresses_count",
    trailing30DayActiveAddressesCount = "trailing_30_day_active_addresses_count",
    dailyMintsDollar = "adjusted_daily_mints_dollar",
    dailyMintsToken = "adjusted_daily_mints_token",
}

export interface PlatformTableData extends Platform {
    rank: number;
    asset_count: number | null;
    rwa_asset_count: number | null;
    stablecoin_asset_count: number | null;
    distributed_market_cap: MeasureValue | null;
    represented_market_cap: MeasureValue | null;
    stablecoin_market_cap: MeasureValue;
    rwa_holders_count: MeasureValue;
    stablecoin_holders_count: MeasureValue;
}

export enum PlatformTableDataColumns {
    id = "id",
    name = "name",
    slug = "slug",
    iconUrl = "icon_url",
    colorHex = "color_hex",
    assetCount = "asset_count",
    description = "description",
    bridgedTokenMarketCapDollar = "bridged_token_market_cap_dollar",
    bridgedTokenValueDollar = "bridged_token_value_dollar",
    circulatingAssetValueDollar = "circulating_asset_value_dollar",
    trailing30DayTransferVolume = "trailing_30_day_transfer_volume",
    holdingAddressesCount = "holding_addresses_count",
    trailing30DayActiveAddressesCount = "trailing_30_day_active_addresses_count",
    dailyMintsDollar = "adjusted_daily_mints_dollar",
    dailyMintsToken = "adjusted_daily_mints_token",
    tokenizationType = "tokenization_type",
    // // // //
    // TableData specific columns
    rwaAssetCount = "rwa_asset_count",
    stablecoinAssetCount = "stablecoin_asset_count",
    distributedMarketCap = "distributed_market_cap",
    representedMarketCap = "represented_market_cap",
    stablecoinMarketCap = "stablecoin_market_cap",
    rwaHoldersCount = "rwa_holders_count",
    stablecoinHoldersCount = "stablecoin_holders_count",
}

/**
 * Normalizes a measure value
 * @param {MeasureValue} mval - The measure value to normalize
 * @returns {MeasureValue|null} The normalized measure value, or null if the input is null
 */
function normalizeMeasureValue(mval: MeasureValue) {
    if (!mval) {
        return null;
    }

    return {
        val: mval.val,
        val_7d: mval.val_7d,
        val_30d: mval.val_30d,
        val_90d: mval.val_90d,
        chg_7d_amt: mval.chg_7d_amt,
        chg_30d_amt: mval.chg_30d_amt,
        chg_90d_amt: mval.chg_90d_amt,
        // Format percent values as decimals, for now
        chg_7d_pct: mval.chg_7d_pct ? mval.chg_7d_pct / 100 : null,
        chg_30d_pct: mval.chg_30d_pct ? mval.chg_30d_pct / 100 : null,
        chg_90d_pct: mval.chg_90d_pct ? mval.chg_90d_pct / 100 : null,
    };
}

/**
 * Subtracts two measure values
 * @param {number|null} mval1 - The first measure value
 * @param {number|null} mval2 - The second measure value
 * @returns {number|null} The result of the subtraction, or null if either value is null
 */
function subtractMeasureValues(mval1: number | null, mval2: number | null) {
    if (mval1 === null) {
        return null;
    }

    if (mval2 === null) {
        return mval1;
    }

    return mval1 - mval2;
}

/**
 * Calculates the number of holders for non-stablecoin assets on a network
 * @param {MeasureValue} holders_count - The current number of holders for non-stablecoin assets
 * @param {MeasureValue} stablecoin_holders_count - The current number of holders for stablecoin assets
 * @returns {MeasureValue|null} The number of holders for non-stablecoin assets, or null if data is unavailable
 */
function getRwaHoldersCount(
    holders_count: MeasureValue,
    stablecoin_holders_count: MeasureValue,
) {
    if (holders_count === null) {
        return null;
    }

    if (stablecoin_holders_count === null) {
        return holders_count;
    }

    // Define values for holders measure
    const val = holders_count.val - stablecoin_holders_count.val;

    // return null if val is 0 or negative
    if (val <= 0) {
        return null;
    }

    const val_7d = subtractMeasureValues(
        holders_count.val_7d,
        stablecoin_holders_count.val_7d,
    );
    const val_30d = subtractMeasureValues(
        holders_count.val_30d,
        stablecoin_holders_count.val_30d,
    );
    const val_90d = subtractMeasureValues(
        holders_count.val_90d,
        stablecoin_holders_count.val_90d,
    );

    const chg_7d_amt = subtractMeasureValues(val, val_7d);
    const chg_30d_amt = subtractMeasureValues(val, val_30d);
    const chg_90d_amt = subtractMeasureValues(val, val_90d);

    const chg_7d_pct = calculatePercentChange({
        initialValue: val_7d,
        finalValue: val,
    });
    const chg_30d_pct = calculatePercentChange({
        initialValue: val_30d,
        finalValue: val,
    });
    const chg_90d_pct = calculatePercentChange({
        initialValue: val_90d,
        finalValue: val,
    });

    return {
        val,
        val_7d,
        val_30d,
        val_90d,
        chg_7d_amt,
        chg_7d_pct,
        chg_30d_amt,
        chg_30d_pct,
        chg_90d_amt,
        chg_90d_pct,
    };
}

export function getPlatformTableData(
    platform: Platform[],
): PlatformTableData[] {
    return platform.map((p, index) => {
        const stablecoin_stats = p.asset_class_stats.find(
            (ac) => ac.id === ASSET_CLASS_IDS.Stablecoin,
        );

        const distributedStat = p.tokenization_type_stats.find(
            (stat) => stat.type === "distributed",
        );

        const representedStat = p.tokenization_type_stats.find(
            (stat) => stat.type === "represented",
        );

        const distributed_rwa_value: MeasureValue | null = distributedStat
            ? distributedStat.bridged_token_value_dollar
            : null;

        const represented_rwa_value: MeasureValue | null = representedStat
            ? representedStat.bridged_token_value_dollar
            : null;

        const asset_count = p.asset_count ? Number(p.asset_count) : 0;

        const rwa_asset_count =
            asset_count - (stablecoin_stats?.asset_count || 0);

        const stablecoin_asset_count = stablecoin_stats?.asset_count || 0;

        const holders_count = normalizeMeasureValue(
            p.holding_addresses_count || null,
        );

        const stablecoin_holders_count = normalizeMeasureValue(
            stablecoin_stats?.holding_addresses_count || null,
        );

        const rwa_holders_count = getRwaHoldersCount(
            holders_count,
            stablecoin_holders_count,
        );

        const row: PlatformTableData = {
            ...p,
            rank: index + 1,
            asset_count: asset_count > 0 ? asset_count : null,
            rwa_asset_count: rwa_asset_count > 0 ? rwa_asset_count : null,
            stablecoin_asset_count:
                stablecoin_asset_count > 0 ? stablecoin_asset_count : null,
            distributed_market_cap: normalizeMeasureValue(
                distributed_rwa_value,
            ),
            represented_market_cap: normalizeMeasureValue(
                represented_rwa_value,
            ),
            stablecoin_market_cap: normalizeMeasureValue(
                stablecoin_stats?.bridged_token_market_cap_dollar || null,
            ),
            rwa_holders_count,
            stablecoin_holders_count: stablecoin_holders_count,
        };

        return row;
    });
}
